import React, { useState, useEffect } from "react";
import { Container, Row, Col, Input, Card, CardBody, } from "reactstrap";
import TopNav from "../../components/Common/TopNav";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Header from "../../components/HorizontalLayout/Header";
import Footer from "../../components/HorizontalLayout/Footer";
import DataTable, { TableColumn } from "react-data-table-component";
import axiosInstance from "src/common/axiosInstance";
import { useSelector, useDispatch } from "react-redux";
import TableCustomLoader from "src/components/Common/TableCustomLoader";
import { useNavigate, useLocation } from "react-router-dom";
import EmailReport from "src/pages/EmailReport/EmailReport";
import { RootState } from "../../slices/store";
import DeleteModal from "src/common/DeleteModal";
import { toast } from 'react-toastify';
import useIsMobile from "src/hooks/useIsMobile";
import "./emailReport.css"
import Pagination from "src/components/Common/Pagination";

type Report = {
  campaign: string;
  email: string;
  subject: string;

};

type EmailReport = {
  id: number;
  campaign_name: string;
  emailIds: string;
  subject: string;
  send_date: string;
  future_send_date?: string;
  report_type: string;
  created_by: number;
  sendDateTime: string;
  send_type: string;
  reportRange: string;
  line_item_ids: number[];
  future_send_Instuction: string;
};

const ListEmailReports = () => {
  const { user, userError } = useSelector((state: any) => state.login);
  const agencyData = useSelector((state: any) => state.agency.agency.data);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportsData, setReportsData] = useState<EmailReport[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState<EmailReport | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const filters = useSelector((state: RootState) => state.filters);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentReport, setCurrentReport] = useState<EmailReport | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const location = useLocation();

  const {
    selectedAgency,
    selectedStatus,
    selectedOrder,
    selectedLineItems,
    selectedMediaType,
    selectedRateType,
    selectedExchangeType,
    startDate,
    endDate,
  } = filters;

  const fetchEmailReports = async (page: number = currentPage, perPage: number = pageSize) => {
    try {
      setLoading(true);
      const userId = user.user.users_id; // or from context/store
      const response = await axiosInstance.get(`/get-email-history/${userId}/${agencyData.Id}`, {
        params: {
          page: page,
          per_page: perPage
        }
      });
      
      // Handle paginated response
      if (response.data.data) {
        setReportsData(response.data.data);
        setTotalRecords(response.data.meta.total);
        setTotalPages(response.data.meta.last_page);
        setCurrentPage(response.data.meta.current_page);
      } else {
        // Fallback for non-paginated response (backwards compatibility)
        setReportsData(response.data);
        setTotalRecords(response.data.length);
        setTotalPages(Math.ceil(response.data.length / perPage));
      }
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch email reports:", error);
      toast.error("Failed to load email reports");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailReports();
  }, []);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchEmailReports();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch new data when pagination changes
  useEffect(() => {
    fetchEmailReports(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const formatSendType = (row: EmailReport): JSX.Element | string => {
    let futureSendInstruction = row.future_send_Instuction;
    let formattedInstruction = "";

    // Normalize sendType to proper case
    const sendType = row.send_type ?
      row.send_type.charAt(0).toUpperCase() + row.send_type.slice(1).toLowerCase() : "";
    const futureSendDate = row.future_send_date;

    if (futureSendInstruction) {
      try {
        const instructionObj = JSON.parse(futureSendInstruction);
        
        // Format time from "08am" to "08AM"
        const formatTime = (time: string) => {
          if (!time) return '';
          return time.toUpperCase();
        };

        // Format based on send type
        if (sendType === "Monthly") {
          const parts = [];
          if (instructionObj.time) {
            parts.push(`Time:${formatTime(instructionObj.time)}`);
          }
          if (instructionObj.month) {
            parts.push(`Date:${instructionObj.month}`);
          }
          formattedInstruction = parts.join(',');
        } 
        else if (sendType === "Weekly") {
          const parts = [];
          if (instructionObj.week) {
            parts.push(`Week:${instructionObj.week}`);
          }
          if (instructionObj.time) {
            parts.push(`Time:${formatTime(instructionObj.time)}`);
          }
          if (instructionObj.days) {
            const days = instructionObj.days.split(',').map((day: string) => {
              const trimmed = day.trim();
              return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
            }).join(',');
            parts.push(`Day:${days}`);
          }
          formattedInstruction = parts.join(',');
        }
        else if (sendType === "Daily") {
          if (instructionObj.time) {
            formattedInstruction = `Time:${formatTime(instructionObj.time)}`;
          }
        }
        else if (sendType === "Ondate") {
          if (instructionObj.time) {
            formattedInstruction = `Time:${formatTime(instructionObj.time)}`;
          }
        }

      } catch (e) {
        console.error("Error parsing future_send_Instuction:", e);
        formattedInstruction = futureSendInstruction;
      }
    }

    if (!sendType && !futureSendDate && !formattedInstruction) {
      return "-";
    }

    // Format the output based on send type
    let displayText = "";
    if (sendType === "Weekly" && futureSendDate && futureSendDate !== '""' && futureSendDate !== "null") {
      // Format: Weekly"09/08/2025" (Week:OneWeek,Time:06PM,Day:Mon,Wed,Tue,Thu,Fri,Sat,Sun)
      displayText = `${sendType}"${futureSendDate}"`;
    } else if (sendType === "Ondate" && futureSendDate && futureSendDate !== '""' && futureSendDate !== "null") {
      // Format: OnDate"09/10/2025" (Time:08AM)
      displayText = `${sendType}"${futureSendDate}"`;
    } else {
      // Format: Monthly (Time:08AM,Date:1,2,3,...) or Daily (Time:08AM)
      displayText = sendType;
    }

    return (
      <div style={{ lineHeight: "1.5", whiteSpace: "normal" }}>
        <strong>{displayText}</strong>
        {formattedInstruction && (
          <span style={{ fontSize: "0.9em", marginLeft: "4px" }}>
            ({formattedInstruction})
          </span>
        )}
      </div>
    );
  };
  const columns: TableColumn<EmailReport>[] = [
    {
      name: "Campaign",
      selector: (row) => row.campaign_name?.toLowerCase() || "", // Proper selector for sorting
      cell: (row) => (
        <div
          title={row.campaign_name}
          style={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: "1.3",
          }}
        >
          {row.campaign_name || "-"}
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Email Address",
      selector: (row) => row.emailIds.toLowerCase(), // Use first email for sorting
      cell: (row) => {
        const emails = row.emailIds.split(",").map((e) => e.trim());
        return (
          <div style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
            {emails.map((email, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < emails.length - 1 ? "3px" : "0",
                  lineHeight: "1.5",
                }}
                title={email}
              >
                {email || "-"}
              </div>
            ))}
          </div>
        );
      },
      sortable: true,
      wrap: true,
    },
    {
      name: "Subject",
      selector: (row) => row.subject?.toLowerCase() || "",
      cell: (row) => (
        <div
          title={row.subject}
          style={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: "1.5",
          }}
        >
          {row.subject || ""}
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Send Date & Time",
      selector: (row) => new Date(row.send_date || 0).getTime(), // Convert to timestamp for proper date sorting
      cell: (row) => (
        <div style={{ lineHeight:"1.5" }}>
          {row.send_date || "-"}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Send Type",
      selector: (row) => {
        if (!row.send_type) return "";

        if (row.future_send_Instuction) {
          try {
            const obj = JSON.parse(row.future_send_Instuction);
            if (obj.month) {
              return `month-${obj.month.padStart(2, "0")}`; // month-01, month-02 ...
            }
          } catch {}
        }

        return row.send_type.toLowerCase();
      },
      cell: (row) => (
        <div style={{ whiteSpace: "normal", lineHeight: "1.5" }}>
          {formatSendType(row)}
        </div>
      ),
      wrap: true,
    },
    {
      name: "Report Range",
      selector: (row) => row.report_type?.toLowerCase() || "",
      cell: (row) => (
        <div
          title={row.report_type}
          style={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: "1.3",
          }}
        >
          {row.report_type}
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex" style={{ whiteSpace: "nowrap" }}>
          <button
            style={{ border: "none", backgroundColor: "transparent" }}
            onClick={() => {
              setCurrentReport(row);
              setDeleteModal(true);
            }}
            title="Delete"
          >
            <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
          </button>
          {row.send_type !== "Sent" && (
            <button
              style={{ border: "none", backgroundColor: "transparent" }}
              onClick={() => handleEditClick(row)}
              title="Edit"
            >
              <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
            </button>
          )}
        </div>
      ),
      width: "120px",
    },
  ];

  const handleEditClick = (row: any) => {
    setEditData(row);
    setIsEditMode(true);
    setShowModal(true);
  };


  const filteredData = reportsData.filter((item: EmailReport) => {
    const searchLower = searchTerm.toLowerCase();

    return (
      item.campaign_name?.toLowerCase().includes(searchLower) ||
      item.emailIds?.toLowerCase().includes(searchLower) ||
      item.subject?.toLowerCase().includes(searchLower) ||
      item.send_date?.toLowerCase().includes(searchLower) ||
      item.send_type?.toLowerCase().includes(searchLower) ||
      item.report_type?.toLowerCase().includes(searchLower) ||
      formatSendType(item).toString().toLowerCase().includes(searchLower)
    );
  });

  const isMobile = useIsMobile();

  const handleDeleteReport = async () => {
    if (currentReport) {
      try {
        await axiosInstance.delete(`/email-report/${currentReport.id}`);
        toast.success("Entry deleted successfully!");
        // Refresh current page after deletion
        await fetchEmailReports(currentPage, pageSize);
      } catch (error: any) {
        console.error("Error deleting entry:", error);
        toast.error(error.response?.data?.message || "Failed to delete entry.");
      }
      setDeleteModal(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditData(null);
  };

  // Note: Pagination is now handled server-side
  // Client-side filtering still applies to the current page data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteReport}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Header />
      <Container fluid style={{ maxWidth: "100%", marginTop: "20px" }}>
        <div className="page-content" style={{ paddingTop: "0" }}>
          <Breadcrumbs title="Reports" breadcrumbItem="Email Reports" titleLink="/reports" />
          <Card style={{ borderRadius: '10px',marginBottom:"50px" }}>
            <CardBody>
              <div className="d-flex justify-content-end mb-3 ">
                <Input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "4px"
                  }}
                  className="responsive-input"
                />
              </div>
              {loading ? (
                <TableCustomLoader length={2}
                  message="Almost ready! Getting email reports data." />
              ) : (
                !isMobile ?
                  <DataTable
                    columns={columns}
                    data={filteredData}
                    highlightOnHover
                    responsive
                    className="data-table email-reports"
                  />
                  :
                  <div className="d-block d-md-none mobile-cards-container">
                    {filteredData.map((user: any) => {
                      return (
                        <div className="mobile-card card " key={user.userId}>
                          <div
                            className="mobile-card-row"
                          >
                            <span className="row-title fw-bold">
                              Campaign
                            </span>
                            <span className="row-value" title={user.campaign_name}>
                              {user.campaign_name}
                            </span>
                          </div>
                          <div
                            className="mobile-card-row "
                          >
                            <span className="row-title fw-bold">
                              Email
                            </span>
                            <span className="row-value">
                              {(user.emailIds || "")
                                .split(",")
                                .map((e: any, idx: number) => e.trim())
                                .map((email: any, idx: number) => (
                                  <div
                                    key={idx}
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      marginBottom: "5px",
                                    }}
                                    title={email}
                                  >
                                    {email || "-"}
                                  </div>
                                ))}
                            </span>
                          </div>
                          <div
                            className="mobile-card-row "
                          >
                            <span className="row-title fw-bold">
                              Subject
                            </span>
                            <span className="row-value">
                              {user.subject}
                            </span>

                          </div>
                          <div
                            className="mobile-card-row "
                          >
                            <span className="row-title fw-bold">
                              Send Date & Time
                            </span>
                            <span className="row-value">
                              {user.send_date || "-"}
                            </span>
                          </div>
                          <div
                            className="mobile-card-row "
                          >
                            <span className="row-title fw-bold">
                              Send Type
                            </span>
                            <span className="row-value">
                              {formatSendType(user)}
                            </span>
                          </div>
                          <div
                            className="mobile-card-row "
                          >
                            <span className="row-title fw-bold">
                              Report Range
                            </span>
                            <span className="row-value">
                              {user.report_type}
                            </span>

                          </div>
                          <div className="mobile-report-action-row">
                            {user.send_type !== "Sent" && (
                              <button
                                onClick={() => handleEditClick(user)}
                                title="Edit"
                                className="edit-btn"
                              >
                                Edit
                                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                              </button>
                            )}
                            <button
                              className="delete-btn"
                              onClick={() => {
                                setCurrentReport(user);
                                setDeleteModal(true);
                              }}
                              title="Delete"
                            >
                              Delete
                              <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
              )}
              {/* 🔹 Pagination */}
              {filteredData.length > 0 &&
                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  currentCount={filteredData.length}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                />
}
            </CardBody>
          </Card>
          {showModal && (
            <EmailReport
              showModal={showModal}
              onClose={handleCloseModal}
              isEditMode={isEditMode}
              editData={editData}
              selectedOrder={selectedOrder}
              selectedItems={editData?.line_item_ids || []}
              currentStartDate={startDate}
              currentEndDate={endDate}
              selectedAgency={selectedAgency}
              reportDataBy={filters.dataBy}
            />
          )}
        </div>
      </Container>
      <Footer />
    </React.Fragment>
  );
};

export default ListEmailReports;
